import { useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import {
  fetch,
  get,
  create,
  AppSubscriptionPeriod,
  AppSubscriptionPeriodCreationPayload,
  GetByPeriodParams,
  getByPeriod,
  getAllPeriods,
  AppSubscriptionPeriodsInterimAgencyTotal,
  calculatePeriodTotals,
  CalculatePeriodTotalsParams,
  AppSubscriptionPeriodsInterimAgencyPeriod,
  update,
} from '../api/app-subscription-periods'
import { getDocIdAsString } from '../utils/documents'

export const appSubscriptionPeriodKeys = {
  create: ['app-subscription-periods', 'create'] as const,
  update: ['app-subscription-periods', 'update'] as const,
}

// Queries
export const useAppSubscriptionPeriodQuery = (
  id?: string,
  options?: QueryOptions<AppSubscriptionPeriod>,
): UseQueryResult<AppSubscriptionPeriod, any> =>
  useResource<AppSubscriptionPeriod>('app-subscription-periods', get, id, undefined, options)

export const useAppSubscriptionPeriodsQuery = (
  filters?: any,
  options?: QueryOptions<AppSubscriptionPeriod[]>,
): UseQueryResult<AppSubscriptionPeriod[], any> =>
  useResources<AppSubscriptionPeriod[], any>('app-subscription-periods', fetch, filters, options)

export const useAppSubscriptionPeriodByPeriodQuery = <T = AppSubscriptionPeriod>(
  params?: GetByPeriodParams,
  options?: QueryOptions<T>,
): UseQueryResult<T, any> =>
  // Important: use a params-based cache key so changes like employerId trigger refetch
  useResources<T, GetByPeriodParams>(
    'app-subscription-periods-by-period',
    getByPeriod as (params: GetByPeriodParams) => Promise<T>,
    params as GetByPeriodParams,
    options,
  )

export const useAppSubscriptionPeriodTotalsQuery = (
  params?: CalculatePeriodTotalsParams,
  options?: QueryOptions<AppSubscriptionPeriodsInterimAgencyTotal>,
): UseQueryResult<AppSubscriptionPeriodsInterimAgencyTotal, any> =>
  useResources<AppSubscriptionPeriodsInterimAgencyTotal, CalculatePeriodTotalsParams>(
    'app-subscription-periods-totals',
    calculatePeriodTotals as (params: CalculatePeriodTotalsParams) => Promise<AppSubscriptionPeriodsInterimAgencyTotal>,
    params as CalculatePeriodTotalsParams,
    {
      ...options,
      enabled: !!params?.interimAgencyId,
    },
  )

/**
 * Hook pour récupérer toutes les périodes d'abonnement d'une agence d'intérim
 */
export const useAppSubscriptionPeriodsByAgencyQuery = (
  interimAgencyId?: string,
  options?: QueryOptions<AppSubscriptionPeriodsInterimAgencyPeriod[]>,
): UseQueryResult<AppSubscriptionPeriodsInterimAgencyPeriod[], any> =>
  useResource<AppSubscriptionPeriodsInterimAgencyPeriod[]>(
    `app-subscription-periods-all-periods`,
    getAllPeriods,
    interimAgencyId,
    undefined,
    {
      ...options,
      enabled: !!interimAgencyId,
    },
  )

// Mutations
const onMutationSuccess = () => {
  queryClient.invalidateQueries(['app-subscription-periods'])
}

export const useCreateAppSubscriptionPeriodMutation = (): UseMutationResult<
  AppSubscriptionPeriod,
  AppSubscriptionPeriodCreationPayload
> =>
  useMutation(create, {
    mutationKey: appSubscriptionPeriodKeys.create,
    onSuccess: onMutationSuccess,
  })

export const useCreateAppSubscriptionPeriodMutationFromTotal = (
  appSubscriptionPeriodsInterimAgencyTotal: AppSubscriptionPeriodsInterimAgencyTotal,
): ((params: { invoiceRef?: string; comment?: string }) => Promise<AppSubscriptionPeriod[]>) => {
  const mutation = useCreateAppSubscriptionPeriodMutation()
  const create = async (params: { invoiceRef?: string; comment?: string }) => {
    const res = await Promise.all(
      appSubscriptionPeriodsInterimAgencyTotal.employerDetails.map(
        async employer =>
          await mutation.mutateAsync({
            ...params,
            employer: getDocIdAsString(employer.employer),
            periodNumber: appSubscriptionPeriodsInterimAgencyTotal.period.periodNumber,
            periodYear: appSubscriptionPeriodsInterimAgencyTotal.period.year,
            interimAgency: getDocIdAsString(
              appSubscriptionPeriodsInterimAgencyTotal.interimAgency,
            ),
          }),
      ),
    )

    queryClient.invalidateQueries(['app-subscription-periods-totals'])
    return res
  }

  return create
}

export const useUpdateAppSubscriptionPeriodMutation = (): UseMutationResult<
  AppSubscriptionPeriod,
  { _id: string; invoiceRef?: string; comment?: string }
> =>
  useMutation(update, {
    mutationKey: appSubscriptionPeriodKeys.update,
    onSuccess: onMutationSuccess,
  })

export const useUpdateAppSubscriptionPeriodMutationFromTotal = (
  appSubscriptionPeriodsInterimAgencyTotal: AppSubscriptionPeriodsInterimAgencyTotal,
): ((params: { invoiceRef?: string; comment?: string }) => Promise<AppSubscriptionPeriod[]>) => {
  const mutation = useUpdateAppSubscriptionPeriodMutation()
  const update = async (params: { invoiceRef?: string; comment?: string }) => {
    const res = await Promise.all(
      appSubscriptionPeriodsInterimAgencyTotal.employerDetails.map(
        async employer =>
          await mutation.mutateAsync({
            ...params,
            _id: employer.appSubscriptionPeriodId as string,
          }),
      ),
    )

    queryClient.invalidateQueries(['app-subscription-periods-totals'])
    return res
  }

  return update
}
